import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useInView } from '../hooks/useInView';

function AnimatedNumber({ target, suffix = '', isInView }) {
  const [current, setCurrent] = useState(0);
  const num = parseInt(target) || 0;

  useEffect(() => {
    if (!isInView) return;
    if (num === 0) { setCurrent(0); return; }
    let start = 0;
    const duration = 2000;
    const step = duration / num;
    const timer = setInterval(() => {
      start++;
      setCurrent(start);
      if (start >= num) clearInterval(timer);
    }, Math.max(step, 20));
    return () => clearInterval(timer);
  }, [isInView, num]);

  return (
    <span className="text-5xl md:text-6xl lg:text-7xl font-900 tracking-tighter tabular-nums">
      {current}{suffix}
    </span>
  );
}

export default function TheNumbers() {
  const { t } = useLanguage();
  const [ref, isInView] = useInView();
  const [rotationIndex, setRotationIndex] = useState(0);
  const [fade, setFade] = useState(true);
  const rotations = t.numbers.rotations;

  useEffect(() => {
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setRotationIndex((prev) => (prev + 1) % rotations.length);
        setFade(true);
      }, 400);
    }, 6000);
    return () => clearInterval(interval);
  }, [rotations.length]);

  useEffect(() => {
    setRotationIndex(0);
    setFade(true);
  }, [rotations]);

  return (
    <section
      id="results"
      ref={ref}
      data-testid="numbers-section"
      className="py-20 bg-[#1A1A2E] relative"
    >
      {/* Subtle radial glow */}
      <div className="absolute inset-0 bg-gradient-radial from-[#E71C25]/[0.03] via-transparent to-transparent" />

      <div className="relative z-10 max-w-6xl mx-auto px-6">
        {/* Stats grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-12 mb-20">
          {t.numbers.stats.map((stat, i) => {
            const numPart = stat.value.replace(/[^0-9]/g, '');
            const suffix = stat.value.replace(/[0-9]/g, '');
            return (
              <div
                key={i}
                data-testid={`stat-${i}`}
                className={`text-center transition-all duration-700 ${
                  isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${i * 150}ms` }}
              >
                <AnimatedNumber target={numPart} suffix={suffix} isInView={isInView} />
                <p className="text-[#B0B0B0] text-sm mt-3 tracking-wide">{stat.label}</p>
              </div>
            );
          })}
        </div>

        {/* Rotating text */}
        <div className="text-center border-t border-white/5 pt-12">
          <p
            data-testid="numbers-rotation"
            className={`text-lg md:text-xl text-white/70 italic font-medium max-w-3xl mx-auto transition-all duration-400 ${
              fade ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
            }`}
          >
            "{rotations[rotationIndex]}"
          </p>
          {/* Rotation dots */}
          <div className="flex justify-center gap-2 mt-6">
            {rotations.map((_, i) => (
              <div
                key={i}
                className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                  i === rotationIndex ? 'bg-[#E71C25] scale-125' : 'bg-white/15'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
